/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetSkillEfficacySettingsRequest,
  GetSkillEfficacySettingsSecurity,
} from "../models/operations/getskillefficacysettings.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildSkillEfficacySettingsQuery,
  prefetchSkillEfficacySettings,
  queryKeySkillEfficacySettings,
  SkillEfficacySettingsQueryData,
} from "./skillEfficacySettings.core.js";
export {
  buildSkillEfficacySettingsQuery,
  prefetchSkillEfficacySettings,
  queryKeySkillEfficacySettings,
  type SkillEfficacySettingsQueryData,
};

export type SkillEfficacySettingsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getSettings skillEfficacy
 *
 * @remarks
 * Get effective organization-wide skill efficacy sampling settings.
 */
export function useSkillEfficacySettings(
  request?: GetSkillEfficacySettingsRequest | undefined,
  security?: GetSkillEfficacySettingsSecurity | undefined,
  options?: QueryHookOptions<
    SkillEfficacySettingsQueryData,
    SkillEfficacySettingsQueryError
  >,
): UseQueryResult<
  SkillEfficacySettingsQueryData,
  SkillEfficacySettingsQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildSkillEfficacySettingsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getSettings skillEfficacy
 *
 * @remarks
 * Get effective organization-wide skill efficacy sampling settings.
 */
export function useSkillEfficacySettingsSuspense(
  request?: GetSkillEfficacySettingsRequest | undefined,
  security?: GetSkillEfficacySettingsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    SkillEfficacySettingsQueryData,
    SkillEfficacySettingsQueryError
  >,
): UseSuspenseQueryResult<
  SkillEfficacySettingsQueryData,
  SkillEfficacySettingsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildSkillEfficacySettingsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setSkillEfficacySettingsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: SkillEfficacySettingsQueryData,
): SkillEfficacySettingsQueryData | undefined {
  const key = queryKeySkillEfficacySettings(...queryKeyBase);

  return client.setQueryData<SkillEfficacySettingsQueryData>(key, data);
}

export function invalidateSkillEfficacySettings(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "skillEfficacy", "getSettings", ...queryKeyBase],
  });
}

export function invalidateAllSkillEfficacySettings(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "skillEfficacy", "getSettings"],
  });
}
